import type { JSX } from 'react';
import { TabBar } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { EpisodeCategory, ServiceCode } from '@common/enums';
import { ActionsPermission } from '@common/enums/actionPermission';
import { findSingleProcessByServiceCode } from '@common/utils/processUtil';
import { getOneActionPermission } from '@frontend/services/api/actionPermissionApiService';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import {
    canReadBariPostQuestionnaireResponses,
    canReadBariPostTotalWeightLoss,
} from '@frontend/services/authorizationService';
import { translateKey } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useNavigateTo } from '@frontend/utils/hooks/useNavigateTo';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { isMedicalProtocolAvailable } from '@frontend/utils/medicalProtocolAvailability';
import type { PatientEpisodeCategoryTab } from './patientEpisodeCategoryTypes';
import { patientEpisodeCategoryTabs } from './patientEpisodeCategoryHeaderTabs';

type PatientEpisodeCategoryHeaderTabBarProps = Readonly<{
    selectedPatientEpisodeCategoryTab: PatientEpisodeCategoryTab;
}>;

export function PatientEpisodeCategoryHeaderTabBar(props: PatientEpisodeCategoryHeaderTabBarProps): JSX.Element | null {
    const abortSignal = useAbortSignal();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const { episodeCategory, patient, processes } = usePatientEpisodeCategoryContext();
    const [authorizedTabs, setAuthorizedTabs] = useState<PatientEpisodeCategoryTab[]>([]);
    const { selectedPatientEpisodeCategoryTab } = props;
    const navigateTo = useNavigateTo();

    useEffect(() => {
        loadTabs();
    }, [episodeCategory, organizationId, processes]);

    if (authorizedTabs.length <= 1) {
        return null;
    }

    return (
        <TabBar>
            {authorizedTabs.map((tab) => (
                <TabBar.Button
                    isSelected={selectedPatientEpisodeCategoryTab.key === tab.key}
                    key={tab.key}
                    onClick={() => setSelectedTab(tab.key)}
                >
                    {translateKey(tab.translationKey)}
                </TabBar.Button>
            ))}
        </TabBar>
    );

    function setSelectedTab(tabName: PatientEpisodeCategoryTab['key']) {
        navigateTo('patientCarePath', { activeTab: tabName, episodeCategory, patientId: patient.id });
    }

    async function loadTabs(): Promise<void> {
        const tabs: PatientEpisodeCategoryTab[] = [];
        for (const tab of patientEpisodeCategoryTabs) {
            if (await hasAccess(tab.key)) {
                tabs.push(tab);
            }
        }
        setAuthorizedTabs(tabs);
    }

    async function hasAccess(key: PatientEpisodeCategoryTab['key']): Promise<boolean> {
        switch (key) {
            case 'carePath':
                return true;
            case 'monitoring':
                return hasMonitoringAccess();
            case 'support':
                return hasSupportAccess();
            case 'medicalProtocol':
                return isMedicalProtocolAvailable(episodeCategory);
            default:
                return false;
        }
    }

    async function hasMonitoringAccess(): Promise<boolean> {
        if (episodeCategory === EpisodeCategory.bariatrics) {
            const bariPostProcess = findSingleProcessByServiceCode(processes, ServiceCode.BariPost);
            if (bariPostProcess) {
                return (
                    (await canReadBariPostTotalWeightLoss(bariPostProcess.id, abortSignal)) ||
                    (await canReadBariPostQuestionnaireResponses(bariPostProcess.id, abortSignal))
                );
            }
        }

        return false;
    }

    async function hasSupportAccess(): Promise<boolean> {
        const organization = await getOneOrganization(organizationId, abortSignal);

        if (!organization.areContactsAndTasksEnabled) {
            return false;
        }

        const actionPermission = await getOneActionPermission({
            action: ActionsPermission.PatientTask,
            query: {
                episodeCategory,
            },
            signal: abortSignal,
        });

        return actionPermission.canRead ?? false;
    }
}
